// src/pages/Ventas.jsx
// ═══════════════════════════════════════════════════════════════════════════════
// 📊 HISTORIAL DE VENTAS - ESTILO eFIT
// Consulta y gestión de ventas realizadas
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect } from "react";
import api from "../services/api";
import Swal from "sweetalert2";
import { useFiscal } from "../hooks/useFiscal";
import { generarHTMLRecibo, abrirVentanaImpresion } from "../utils/recibo.utils";

const Ventas = () => {
  const fiscal = useFiscal();
  const [ventas, setVentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ventaSeleccionada, setVentaSeleccionada] = useState(null);
  const [modalDetalle, setModalDetalle] = useState(false);
  const [config, setConfig] = useState({});
  const [filtros, setFiltros] = useState({
    fecha_inicio: "",
    fecha_fin: "",
    estado: "",
  });

  const usuario = JSON.parse(localStorage.getItem("usuario"));

  useEffect(() => {
    cargarVentas();
  }, []);

  const cargarVentas = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filtros.fecha_inicio) params.append("fecha_inicio", filtros.fecha_inicio);
      if (filtros.fecha_fin) params.append("fecha_fin", filtros.fecha_fin);
      if (filtros.estado) params.append("estado", filtros.estado);

      const res = await api.get(`/ventas?${params.toString()}`);
      
      if (res.data.ok && Array.isArray(res.data.ventas)) {
        setVentas(res.data.ventas);
      } else {
        setVentas([]);
      }
    } catch (error) {
      console.error("Error al cargar ventas:", error);
      setVentas([]);  // ✅ Array vacío en caso de error
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar las ventas",
        confirmButtonColor: "#3b82f6",
      });
    } finally {
      setLoading(false);
    }
  };

  const verDetalle = async (id) => {
    try {
     const res = await api.get(`/ventas/${id}`);
setVentaSeleccionada(res.data.venta);  
      setModalDetalle(true);
    } catch (error) {
      console.error("Error al cargar detalle:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo cargar el detalle de la venta",
        confirmButtonColor: "#3b82f6",
      });
    }
  };

 const anularVenta = async (id) => {
  const { value: motivo } = await Swal.fire({
    icon: "warning",
    title: '<span style="color: #dc2626;">⚠️ ¿Anular venta?</span>',
    html: `
      <div style="text-align: left; padding: 10px 0;">
        <p style="margin-bottom: 15px; color: #64748b;">
          Esta acción no se puede deshacer y el stock será restaurado.
        </p>
        <label style="display: block; margin-bottom: 8px; font-weight: 600; color: #374151;">
          📝 Motivo de anulación: <span style="color: #ef4444;">*</span>
        </label>
        <textarea 
          id="motivo" 
          class="swal2-textarea" 
          placeholder="Escribe el motivo de la anulación..."
          style="width: 100%; padding: 12px; border-radius: 10px; border: 2px solid #fca5a5; font-size: 14px; min-height: 100px; margin: 0; resize: none; background: #fef2f2;"
        ></textarea>
      </div>
    `,
    showCancelButton: true,
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#64748b",
    confirmButtonText: "❌ Sí, anular",
    cancelButtonText: "Cancelar",
    width: '480px',
    preConfirm: () => {
      const motivo = document.getElementById("motivo").value;
      if (!motivo) {
        Swal.showValidationMessage("⚠️ Debes proporcionar un motivo");
      }
      return motivo;
    },
  });

  if (!motivo) return;

  try {
    const res = await api.post(`/ventas/${id}/anular`, { motivo });
    const { ncf_nota_credito, ncf_afectado } = res.data;

    Swal.fire({
      icon: "success",
      title: "Venta anulada",
      html: `
        <div style="text-align:left; padding:5px 0;">
          <p style="margin:6px 0;">La venta ha sido anulada correctamente.</p>

          ${ncf_nota_credito ? `
            <p style="margin:6px 0;">
              <strong>🧾 Nota de Crédito:</strong> ${ncf_nota_credito}
            </p>
          ` : ""}

          ${ncf_afectado ? `
            <p style="margin:6px 0;">
              <strong>🔗 Afecta al NCF:</strong> ${ncf_afectado}
            </p>
          ` : ""}
        </div>
      `,
      confirmButtonColor: "#3b82f6",
    });

    cargarVentas();
    if (modalDetalle) setModalDetalle(false);

  } catch (error) {
    console.error("Error al anular venta:", error);
    Swal.fire({
      icon: "error",
      title: "Error",
      text: error.response?.data?.message || error.response?.data?.error || "No se pudo anular la venta",
      confirmButtonColor: "#3b82f6",
    });
  }
};


  const formatearFecha = (fecha) => {
    return new Date(fecha).toLocaleString("es-DO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatearMoneda = (monto) => {
    return new Intl.NumberFormat("es-DO", {
      style: "currency",
      currency: fiscal.moneda?.codigo || "DOP",
    }).format(monto);
  };
  const imprimirRecibo = (venta) => {
    // generarHTMLRecibo usa det.total_linea (correcto con descuento incluido)
    // y muestra tipo_ncf + descuento global. abrirVentanaImpresion usa
    // window.onload para no imprimir antes de que cargue el contenido.
    const html = generarHTMLRecibo(venta, config);
    abrirVentanaImpresion(html);
  };

// ═══════════════════════════════════════════════════════════════════════════
// 🧾 IMPRIMIR NOTA DE CRÉDITO (B04) – REIMPRESIÓN
// ═══════════════════════════════════════════════════════════════════════════
const imprimirNotaCredito = (venta) => {
  // Reutiliza generarHTMLRecibo con esNotaCredito=true:
  // cambia el título a "NOTA DE CRÉDITO (B04)", oculta sección de pago
  // y muestra el NCF B04 con el comprobante que afecta.
  const html = generarHTMLRecibo(venta, config, { esNotaCredito: true });
  abrirVentanaImpresion(html);
};



return (
  
  <div style={styles.container}>

    <div style={styles.mainContent}>
      {/* Filtros */}
      <div style={styles.filtrosCard}>
        <div style={styles.filtrosHeader}>
          <span style={styles.filtrosIcon}>🔍</span>
          <span style={styles.filtrosTitle}>Filtros de búsqueda</span>
        </div>
        <div style={styles.filtrosGrid}>
          <div style={styles.formGroup}>
            <label style={styles.label}>📅 Fecha Inicio</label>
            <input
              type="date"
              value={filtros.fecha_inicio}
              onChange={(e) =>
                setFiltros({ ...filtros, fecha_inicio: e.target.value })
              }
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>📅 Fecha Fin</label>
            <input
              type="date"
              value={filtros.fecha_fin}
              onChange={(e) =>
                setFiltros({ ...filtros, fecha_fin: e.target.value })}
              style={styles.input}
            />
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>📌 Estado</label>
            <select
              value={filtros.estado}
              onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
              style={styles.select}
            >
              <option value="">Todos</option>
              <option value="completada">✅ Completada</option>
              <option value="anulada">❌ Anulada</option>
            </select>
          </div>

          <div style={styles.filtrosActions}>
            <button 
              onClick={cargarVentas}
              style={styles.btnFiltrar}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(59, 130, 246, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.2)';
              }}
            >
              🔍 Filtrar
            </button>
            <button
              onClick={() => {
                setFiltros({ fecha_inicio: "", fecha_fin: "", estado: "" });
                setTimeout(() => cargarVentas(), 100);
              }}
              style={styles.btnLimpiar}
            >
              🗑️ Limpiar
            </button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      {loading ? (
        <div style={styles.loadingBox}>
          <div style={styles.spinner}></div>
          <p>Cargando ventas...</p>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.tableHeader}>
                <th style={styles.th}>Número</th>
                <th style={styles.th}>Fecha</th>
                <th style={styles.th}>Cliente</th>
                <th style={styles.th}>Total</th>
                <th style={styles.th}>Método</th>
                <th style={styles.th}>Estado</th>
                <th style={styles.th}>Cajero</th>
                <th style={styles.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {ventas.length === 0 ? (
                <tr>
                  <td colSpan="8" style={styles.emptyCell}>
                    <div style={styles.emptyIcon}>📭</div>
                    <div>No hay ventas registradas</div>
                  </td>
                </tr>
              ) : (
                ventas.map((venta, index) => (
                  <tr 
                    key={venta.id} 
                    style={{
                      ...styles.tableRow,
                      animation: `fadeIn 0.3s ease forwards`,
                      animationDelay: `${index * 0.03}s`,
                      opacity: 0,
                    }}
                  >
                    <td style={styles.td}>
                      <strong style={styles.numeroVenta}>{venta.numero_venta}</strong>
                    </td>
                    <td style={styles.td}>{formatearFecha(venta.fecha)}</td>
                    <td style={styles.td}>{venta.cliente_nombre || "Cliente General"}</td>
                    <td style={styles.td}>
                      <span style={styles.montoTotal}>
                        {formatearMoneda(venta.total)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.metodoPago}>
                        {venta.metodo_pago === 'efectivo' && '💵'}
                        {venta.metodo_pago === 'tarjeta' && '💳'}
                        {venta.metodo_pago === 'transferencia' && '🏦'}
                        {venta.metodo_pago === 'mixto' && '🔄'}
                        {' '}{venta.metodo_pago}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        background: venta.estado === "completada" 
                          ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)' 
                          : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                        color: venta.estado === "completada" ? "#065f46" : "#991b1b",
                        border: `1px solid ${venta.estado === "completada" ? '#6ee7b7' : '#fca5a5'}`,
                      }}>
                        {venta.estado === "completada" ? "✅ Completada" : "❌ Anulada"}
                      </span>
                    </td>
                    <td style={styles.td}>{venta.usuario_nombre}</td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button
                          onClick={() => verDetalle(venta.id)}
                          style={styles.btnDetalle}
                          title="Ver detalle"
                        >
                          👁️
                        </button>
                        {venta.estado === "completada" &&
                          ["admin", "supervisor", "superadmin"].includes(usuario?.rol) && (
                            <button
                              onClick={() => anularVenta(venta.id)}
                              style={styles.btnAnular}
                              title="Anular venta"
                            >
                              ❌
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>

    {/* Modal Detalle */}
    {modalDetalle && ventaSeleccionada && (
      <div style={styles.modalOverlay} onClick={() => setModalDetalle(false)}>
        <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div style={styles.modalHeader}>

            {/* 🔹 BOTONES DE REIMPRESIÓN */}
            <div style={{ display: "flex", gap: "10px", marginTop: "10px" }}>
              
              {/* 🖨️ Reimprimir Recibo */}
              <button
                onClick={() => imprimirRecibo(ventaSeleccionada)}
                style={{
                  padding: "8px 14px",
                  background: "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px"
                }}
              >
                🖨️ Reimprimir Recibo
              </button>

              {/* 🧾 Reimprimir Nota de Crédito */}
              {ventaSeleccionada.ncf_nota_credito && (
                <button
                  onClick={() => imprimirNotaCredito(ventaSeleccionada)}
                  style={{
                    padding: "8px 14px",
                    background: "#b91c1c",
                    color: "white",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px"
                  }}
                >
                  🧾 Reimprimir Nota de Crédito
                </button>
              )}

            </div>

            <div>
              <h2 style={styles.modalTitle}>📋 Detalle de Venta</h2>
              <p style={styles.modalSubtitle}>
                {ventaSeleccionada.numero_venta}
              </p>
            </div>

            <button
              onClick={() => setModalDetalle(false)}
              style={styles.btnCerrar}
            >
              ✕
            </button>
          </div>

          <div style={styles.modalBody}>
            {/* Info General */}
            <div style={styles.infoSection}>
              <h3 style={styles.sectionTitle}>
                <span style={styles.sectionIcon}>📌</span>
                Información General
              </h3>
              <div style={styles.infoGrid}>
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>Fecha</span>
                  <span style={styles.infoValue}>
                    {formatearFecha(ventaSeleccionada.fecha)}
                  </span>
                </div>
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>Cliente</span>
                  <span style={styles.infoValue}>
                    {ventaSeleccionada.cliente_nombre || "Cliente General"}
                  </span>
                </div>
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>Cajero</span>
                  <span style={styles.infoValue}>
                    {ventaSeleccionada.usuario_nombre}
                  </span>
                </div>
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>Método de Pago</span>
                  <span style={styles.infoValue}>
                    {ventaSeleccionada.metodo_pago}
                  </span>
                </div>
                <div style={styles.infoItem}>
                  <span style={styles.infoLabel}>Estado</span>
                  <span style={{
                    ...styles.badge,
                    background: ventaSeleccionada.estado === "completada"
                      ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
                      : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                    color: ventaSeleccionada.estado === "completada" ? "#065f46" : "#991b1b",
                  }}>
                    {ventaSeleccionada.estado === "completada" ? "✅ Completada" : "❌ Anulada"}
                  </span>
                </div>
              </div>
            </div>

            {/* Comprobante Fiscal */}
            {(ventaSeleccionada.ncf || ventaSeleccionada.ncf_nota_credito) && (
              <div style={styles.infoSection}>
                <h3 style={styles.sectionTitle}>
                  <span style={styles.sectionIcon}>🧾</span>
                  Comprobante Fiscal
                </h3>

                <div style={styles.infoGrid}>

                  {ventaSeleccionada.ncf && (
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>NCF</span>
                      <span style={{ ...styles.infoValue, color: "#1e40af" }}>
                        {ventaSeleccionada.ncf}
                      </span>
                    </div>
                  )}

                  {ventaSeleccionada.ncf_nota_credito && (
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>Nota de Crédito (B04)</span>
                      <span style={{ ...styles.infoValue, color: "#b91c1c" }}>
                        {ventaSeleccionada.ncf_nota_credito}
                      </span>
                    </div>
                  )}

                  {ventaSeleccionada.ncf_afectado && (
                    <div style={styles.infoItem}>
                      <span style={styles.infoLabel}>Afecta a</span>
                      <span style={{ ...styles.infoValue, color: "#7c2d12" }}>
                        {ventaSeleccionada.ncf_afectado}
                      </span>
                    </div>
                  )}

                </div>
              </div>
            )}

            {/* Productos */}
            <div style={styles.infoSection}>
              <h3 style={styles.sectionTitle}>
                <span style={styles.sectionIcon}>📦</span>
                Productos
              </h3>
              <div style={styles.productosTable}>
                <table style={styles.table}>
                  <thead>
                    <tr style={styles.tableHeader}>
                      <th style={styles.th}>Producto</th>
                      <th style={{ ...styles.th, textAlign: "center" }}>Cantidad</th>
                      <th style={{ ...styles.th, textAlign: "right" }}>Precio</th>
                      <th style={{ ...styles.th, textAlign: "right" }}>Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventaSeleccionada.detalles?.map((det, idx) => (
                      <tr key={idx} style={styles.tableRow}>
                        <td style={styles.td}>{det.material_nombre}</td>
                        <td style={{ ...styles.td, textAlign: "center" }}>{det.cantidad}</td>
                        <td style={{ ...styles.td, textAlign: "right" }}>
                          {formatearMoneda(det.precio_unitario)}
                        </td>
                        <td style={{ ...styles.td, textAlign: "right", fontWeight: "700", color: "#1e3a8a" }}>
                          {formatearMoneda(det.subtotal_linea || det.cantidad * det.precio_unitario)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totales */}
            <div style={styles.totalesSection}>
              <div style={styles.totalRow}>
                <span>Subtotal</span>
                <span>{formatearMoneda(ventaSeleccionada.subtotal)}</span>
              </div>

              {ventaSeleccionada.itbis > 0 && (
                <div style={styles.totalRow}>
                  <span>{fiscal.impuesto_label || 'ITBIS'}</span>
                  <span>{formatearMoneda(ventaSeleccionada.itbis)}</span>
                </div>
              )}

              {ventaSeleccionada.descuento > 0 && (
                <div style={{ ...styles.totalRow, color: "#dc2626" }}>
                  <span>Descuento</span>
                  <span>-{formatearMoneda(ventaSeleccionada.descuento)}</span>
                </div>
              )}

              <div style={styles.totalRowFinal}>
                <span>TOTAL</span>
                <span>{formatearMoneda(ventaSeleccionada.total)}</span>
              </div>

              {ventaSeleccionada.metodo_pago === "efectivo" && (
                <>
                  <div style={{
                    ...styles.totalRow,
                    marginTop: "16px",
                    paddingTop: "16px",
                    borderTop: "2px dashed #e2e8f0"
                  }}>
                    <span>Monto Recibido</span>
                    <span>{formatearMoneda(ventaSeleccionada.monto_recibido)}</span>
                  </div>
                  <div style={{ ...styles.totalRow, color: "#059669" }}>
                    <span>Cambio</span>
                    <span>{formatearMoneda(ventaSeleccionada.cambio)}</span>
                  </div>
                </>
              )}
            </div>

            {/* Anulación */}
            {ventaSeleccionada.estado === "anulada" && (
              <div style={styles.anulacionBox}>
                <h4 style={styles.anulacionTitle}>
                  ❌ Venta Anulada
                </h4>
                <p style={styles.anulacionMotivo}>
                  <strong>Motivo:</strong> {ventaSeleccionada.motivo_anulacion}
                </p>
                <p style={styles.anulacionFecha}>
                  Anulada el {formatearFecha(ventaSeleccionada.fecha_anulacion)}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    )}

    <style>{`
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

};

// ═══════════════════════════════════════════════════════════════════════════════
// 🎨 ESTILOS eFIT - PALETA AZUL CORPORATIVA
// ═══════════════════════════════════════════════════════════════════════════════

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)",
    fontFamily: "'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif",
  },

  // Header
  header: {
    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
    padding: "28px 40px",
    boxShadow: "0 4px 20px rgba(30, 58, 138, 0.3)",
  },
  headerContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    maxWidth: "1600px",
    margin: "0 auto",
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },
  headerIconBox: {
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(10px)",
    padding: "16px",
    borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.2)",
  },
  headerIcon: {
    fontSize: "36px",
  },
  title: {
    fontSize: "32px",
    fontWeight: "800",
    color: "white",
    margin: "0",
  },
  subtitle: {
    fontSize: "15px",
    color: "rgba(255, 255, 255, 0.85)",
    margin: "6px 0 0 0",
  },
  headerStats: {
    display: "flex",
    gap: "12px",
  },
  statBadge: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "12px 20px",
    background: "rgba(255, 255, 255, 0.15)",
    borderRadius: "12px",
    color: "white",
    fontSize: "14px",
    fontWeight: "600",
  },

  // Main Content
  mainContent: {
    padding: "32px 40px",
    maxWidth: "1600px",
    margin: "0 auto",
  },

  // Filtros
  filtrosCard: {
    background: "white",
    borderRadius: "18px",
    padding: "24px",
    marginBottom: "24px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    border: "1px solid rgba(59, 130, 246, 0.1)",
  },
  filtrosHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "20px",
  },
  filtrosIcon: {
    fontSize: "20px",
  },
  filtrosTitle: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#1e293b",
  },
  filtrosGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "16px",
    alignItems: "end",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#475569",
    marginBottom: "8px",
  },
  input: {
    padding: "12px 14px",
    fontSize: "14px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    outline: "none",
    transition: "all 0.2s ease",
    background: "#f8fafc",
  },
  select: {
    padding: "12px 14px",
    fontSize: "14px",
    border: "2px solid #e2e8f0",
    borderRadius: "10px",
    outline: "none",
    background: "#f8fafc",
    cursor: "pointer",
  },
  filtrosActions: {
    display: "flex",
    gap: "10px",
  },
  btnFiltrar: {
    padding: "12px 24px",
    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 12px rgba(59, 130, 246, 0.2)",
  },
  btnLimpiar: {
    padding: "12px 24px",
    background: "#f1f5f9",
    color: "#475569",
    border: "none",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
  },

  // Loading
  loadingBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px",
    color: "#64748b",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "4px solid #e2e8f0",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    marginBottom: "16px",
  },

  // Table
  tableContainer: {
    background: "white",
    borderRadius: "18px",
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    overflow: "hidden",
    border: "1px solid rgba(59, 130, 246, 0.1)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)",
  },
  th: {
    padding: "16px 20px",
    textAlign: "left",
    fontSize: "13px",
    fontWeight: "700",
    color: "#1e3a8a",
    borderBottom: "2px solid #e2e8f0",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  tableRow: {
    borderBottom: "1px solid #f1f5f9",
    transition: "all 0.2s ease",
  },
  td: {
    padding: "16px 20px",
    fontSize: "14px",
    color: "#1e293b",
  },
  emptyCell: {
    padding: "60px",
    textAlign: "center",
    color: "#94a3b8",
    fontSize: "16px",
  },
  emptyIcon: {
    fontSize: "48px",
    marginBottom: "12px",
  },
  numeroVenta: {
    color: "#1e3a8a",
    fontSize: "15px",
  },
  montoTotal: {
    fontWeight: "700",
    color: "#059669",
    fontSize: "15px",
  },
  metodoPago: {
    fontSize: "13px",
    color: "#64748b",
    textTransform: "capitalize",
  },
  badge: {
    display: "inline-block",
    padding: "6px 14px",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "700",
  },
  actions: {
    display: "flex",
    gap: "8px",
  },
  btnDetalle: {
    padding: "8px 14px",
    background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
    border: "1px solid #93c5fd",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s ease",
  },
  btnAnular: {
    padding: "8px 14px",
    background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
    border: "1px solid #fca5a5",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    transition: "all 0.2s ease",
  },

  // Modal
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.6)",
    backdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modalContent: {
    background: "white",
    borderRadius: "24px",
    width: "100%",
    maxWidth: "900px",
    maxHeight: "90vh",
    overflow: "auto",
    boxShadow: "0 25px 80px rgba(0, 0, 0, 0.3)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "24px 32px",
    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
    color: "white",
  },
  modalTitle: {
    fontSize: "22px",
    fontWeight: "700",
    margin: 0,
  },
  modalSubtitle: {
    fontSize: "14px",
    opacity: "0.85",
    margin: "4px 0 0 0",
  },
  btnCerrar: {
    background: "rgba(255,255,255,0.2)",
    border: "none",
    borderRadius: "50%",
    width: "40px",
    height: "40px",
    fontSize: "20px",
    cursor: "pointer",
    color: "white",
    transition: "all 0.2s ease",
  },
  modalBody: {
    padding: "32px",
  },
  infoSection: {
    marginBottom: "28px",
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "17px",
    fontWeight: "700",
    color: "#1e293b",
    marginBottom: "16px",
  },
  sectionIcon: {
    fontSize: "20px",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "16px",
  },
  infoItem: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "14px 18px",
    background: "#f8fafc",
    borderRadius: "12px",
  },
  infoLabel: {
    fontSize: "12px",
    color: "#64748b",
    fontWeight: "600",
    textTransform: "uppercase",
  },
  infoValue: {
    fontSize: "15px",
    color: "#1e293b",
    fontWeight: "600",
  },
  productosTable: {
    overflowX: "auto",
    borderRadius: "12px",
    border: "1px solid #e2e8f0",
  },
  totalesSection: {
    background: "linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)",
    padding: "24px",
    borderRadius: "16px",
    border: "1px solid #dbeafe",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "15px",
    color: "#64748b",
    marginBottom: "10px",
  },
  totalRowFinal: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "22px",
    fontWeight: "800",
    color: "#1e3a8a",
    paddingTop: "16px",
    borderTop: "2px solid #3b82f6",
    marginTop: "12px",
  },
  anulacionBox: {
    background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
    border: "2px solid #ef4444",
    borderRadius: "14px",
    padding: "20px",
    marginTop: "24px",
  },
  anulacionTitle: {
    margin: "0 0 12px 0",
    color: "#991b1b",
    fontSize: "18px",
    fontWeight: "700",
  },
  anulacionMotivo: {
    margin: "0 0 8px 0",
    fontSize: "14px",
    color: "#7f1d1d",
  },
  anulacionFecha: {
    margin: "0",
    fontSize: "12px",
    color: "#991b1b",
    opacity: "0.8",
  },
};

export default Ventas;